import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import RankingModal from './RankingModal';
import DropdownMenu from './DropdownMenu';
import VisualizationModal from './VisualizationModal';
import ProgressCircles from './ProgressCircles';
import LifeBar from './LifeBar';
import useGameEngine from './hooks/useGameEngine';
import useSaveGameResult from './hooks/useSaveGameResult';
import { createFiniteMode } from './gameModes/finiteMode';
import { createInfiniteMode } from './gameModes/infiniteMode';
import { useTheme } from './ThemeContext';

import './Game.css';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const GAME_MODES = { FINITE: 'finite', INFINITE: 'infinite' };

const RANK_MEDALS = ['🥇', '🥈', '🥉'];

function playLeaderboardChime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const notes = [523, 659, 784, 1047]; // C5, E5, G5, C6
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = freq;
      const t = ctx.currentTime + i * 0.08;
      gain.gain.setValueAtTime(0.18, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
      osc.start(t);
      osc.stop(t + 0.3);
    });
  } catch (e) {}
}

const Game = ({ user }) => {
  const location = useLocation();
  const { username, roomId, roomName, isMultiplayer, usernames, userId, picture, sessionId: sessionIdFromState, gameMode: roomGameMode, timeHorizon: roomTimeHorizon } = location.state || {};

  // In multiplayer, game mode is fixed by the room; in solo, the player can switch.
  const [gameMode, setGameMode] = useState(isMultiplayer ? (roomGameMode || GAME_MODES.FINITE) : GAME_MODES.FINITE);
  const [selectedTimeHorizon, setSelectedTimeHorizon] = useState(isMultiplayer ? (roomTimeHorizon ?? 10) : 10);
  const [isVisualizationModalOpen, setIsVisualizationModalOpen] = useState(false);
  const [isRankingModalOpen, setIsRankingModalOpen] = useState(false);
  const [rankingData, setRankingData] = useState(null);
  const [highlightedGameId, setHighlightedGameId] = useState(null);
  const [currentScore, setCurrentScore] = useState(null);
  const [websocket, setWebSocket] = useState(null);
  const [liveRankings, setLiveRankings] = useState([]);
  const [leaderboardFlash, setLeaderboardFlash] = useState(false);
  const [finishNotification, setFinishNotification] = useState(null);

  const notificationTimerRef = useRef(null);
  const prevTopRef = useRef(null);

  const [globalUserId] = useState(() => {
    const storedGlobalUserId = localStorage.getItem('soloGlobalUserId');
    return storedGlobalUserId || uuidv4();
  });

  const [sessionId] = useState(() => {
    if (isMultiplayer) return null;
    return sessionIdFromState || uuidv4();
  });

  useEffect(() => {
    if (!isMultiplayer) {
      localStorage.setItem('soloGlobalUserId', globalUserId);
    }
  }, [globalUserId, isMultiplayer]);

  // Effect 1: create the WebSocket connection once
  useEffect(() => {
    if (!isMultiplayer) return;
    const isLocalDev = (process.env.REACT_APP_BACKEND_URL_NO_PORT || '').includes('localhost');
    const protocol = isLocalDev ? 'ws' : 'wss';
    const ws = new WebSocket(`wss://${process.env.REACT_APP_BACKEND_URL_NO_PORT}/ws/${roomName}`);
    ws.onopen = () => console.log('WebSocket connection opened');
    setWebSocket(ws);
    return () => { ws.close(); };
  }, [roomName, isMultiplayer]);

  // Effect 2b: fetch initial live rankings on mount so the leaderboard is populated
  // even when the component remounts (e.g. returning from the Rankings page)
  useEffect(() => {
    if (!isMultiplayer) return;
    const isLocalDev = backendUrl.includes('localhost') || backendUrl.includes('127.0.0.1');
    const secureBackendUrl = isLocalDev ? backendUrl : backendUrl.replace('http://', 'https://');
    const url = gameMode === GAME_MODES.INFINITE
      ? `${secureBackendUrl}/room_leaderboard_infinite/${roomId}`
      : `${secureBackendUrl}/room_leaderboard/${roomId}/${selectedTimeHorizon}`;
    axios.get(url)
      .then(response => setLiveRankings(response.data))
      .catch(err => console.error('Initial live rankings fetch error:', err));
  }, [isMultiplayer, roomId, gameMode, selectedTimeHorizon]);

  // Effect 2: attach onmessage handler — re-runs whenever deps that the handler uses change
  useEffect(() => {
    if (!websocket) return;
    const isLocalDev = backendUrl.includes('localhost') || backendUrl.includes('127.0.0.1');
    const secureBackendUrl = isLocalDev ? backendUrl : backendUrl.replace('http://', 'https://');

    websocket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.playerProgress?.progress !== 100) return;

        const finishedUsername = data.playerProgress.username;
        const th = data.playerProgress.timeHorizon ?? selectedTimeHorizon;
        const url = gameMode === GAME_MODES.INFINITE
          ? `${secureBackendUrl}/room_leaderboard_infinite/${roomId}`
          : `${secureBackendUrl}/room_leaderboard/${roomId}/${th}`;

        axios.get(url)
          .then(response => {
            const newRankings = response.data;
            setLiveRankings(newRankings);
            if (!newRankings.length) return;

            const newTop = newRankings[0];
            const prevTop = prevTopRef.current;
            const scoreField = gameMode === GAME_MODES.INFINITE ? 'click_count' : 'mean_absolute_error';
            const newScore = newTop[scoreField];
            const prevScore = prevTop?.[scoreField];
            const topChanged = !prevTop
              || newTop.username !== prevTop.username
              || (gameMode === GAME_MODES.INFINITE ? newScore > prevScore : newScore < prevScore);

            if (topChanged) {
              prevTopRef.current = { username: newTop.username, [scoreField]: newScore };
              setLeaderboardFlash(true);
              playLeaderboardChime();
              setTimeout(() => setLeaderboardFlash(false), 1200);
              clearTimeout(notificationTimerRef.current);
              const msg = finishedUsername === username
                ? "You're on top! 🥇"
                : `🏆 ${newTop.username} is on top!`;
              setFinishNotification(msg);
              notificationTimerRef.current = setTimeout(() => setFinishNotification(null), 3000);
            }
          })
          .catch(err => console.error('Live rankings fetch error:', err));
      } catch (e) { console.error('WS message parse error:', e); }
    };
  }, [websocket, roomId, gameMode, selectedTimeHorizon, username]);

  const fetchRankingData = useCallback((timeHorizon) => {
    if (isMultiplayer) {
      const isLocalDev = backendUrl.includes('localhost') || backendUrl.includes('127.0.0.1');
      const secureBackendUrl = isLocalDev ? backendUrl : backendUrl.replace('http://', 'https://');
      const url = gameMode === GAME_MODES.INFINITE
        ? `${secureBackendUrl}/room_leaderboard_infinite/${roomId}`
        : `${secureBackendUrl}/room_leaderboard/${roomId}/${timeHorizon}`;
      axios.get(url)
        .then(response => {
          setRankingData({ timeHorizon, ranking: response.data });
        })
        .catch(error => console.error('Error fetching ranking data:', error));
    }
  }, [roomId, isMultiplayer, gameMode]);

  const handleMultiplayerSaved = useCallback(({ gameId, timeHorizon }) => {
    setHighlightedGameId(gameId);
    fetchRankingData(timeHorizon);
  }, [fetchRankingData]);

  const { saveGameResult } = useSaveGameResult({
    isMultiplayer,
    userId,
    roomId,
    username,
    websocket,
    sessionId,
    globalUserId,
    onMultiplayerSaved: handleMultiplayerSaved,
  });

  const modeConfig = useMemo(
    () => gameMode === GAME_MODES.FINITE
      ? createFiniteMode(selectedTimeHorizon)
      : createInfiniteMode(),
    [gameMode, selectedTimeHorizon]
  );

  const handleGameOver = useCallback((score, _timeArray) => {
    const scoreWithMeta = {
      ...score,
      timeHorizon: gameMode === GAME_MODES.FINITE ? selectedTimeHorizon : null,
    };
    setCurrentScore(scoreWithMeta);
    setIsVisualizationModalOpen(true);
    saveGameResult(scoreWithMeta);
  }, [gameMode, selectedTimeHorizon, saveGameResult]);

  const { theme, isAscii: isAnyAscii } = useTheme();
  const isDarkTheme = theme === 'dark';
  const isAscii = isAnyAscii && isDarkTheme;
  const isAsciiLight = isAnyAscii && !isDarkTheme;
  const asciiColor = isAscii ? '#33ff33' : '#1a1a1a';

  const { isRunning, clickCount, timeArray, modeState, handleClick, reset } = useGameEngine(modeConfig, handleGameOver);

  const handleTryAgain = useCallback(() => {
    reset();
    setCurrentScore(null);
  }, [reset]);

  const handleTimeHorizonChange = useCallback((t) => {
    setSelectedTimeHorizon(t);
    reset();
    setCurrentScore(null);
  }, [reset]);

  const handleGameModeChange = useCallback((mode) => {
    setGameMode(mode);
    reset();
    setCurrentScore(null);
  }, [reset]);

  const handleVisualizationModalClose = useCallback(() => {
    setIsVisualizationModalOpen(false);
    if (isMultiplayer) setIsRankingModalOpen(true);
    reset();
    setCurrentScore(null);
  }, [isMultiplayer, reset]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const textInButton = useMemo(() => (isRunning ? 'Click me!' : 'Start'), [isRunning]);

  // For the visualization modal in infinite mode, pass actual click count so
  // TimingVisualization / ProgressCircles renders correctly
  const modalTimeHorizon = currentScore?.timeHorizon ?? timeArray.length;

  return (
    <div className="game-container" style={{ position: 'relative', paddingTop: '64px', boxSizing: 'border-box' }}>
      <DropdownMenu user={{ id: userId, username, picture }} isSoloMode={!isMultiplayer} />

      <div className="header-section">
        <div className="welcome-message">
          {isMultiplayer ? `Hello ${username} - Room: ${roomName}` : 'Click every second'}
        </div>
        {isAnyAscii && (
          <div style={{ display: 'flex', alignItems: 'center', width: '100%', margin: '2px 0 8px', color: asciiColor, fontFamily: '"Courier New", Courier, monospace', fontSize: '0.9rem' }}>
            <span style={{ flexShrink: 0 }}>├</span>
            <span style={{ flex: 1, borderTop: `1px solid ${asciiColor}`, display: 'block' }} />
            <span style={{ flexShrink: 0 }}>┤</span>
          </div>
        )}

        {isMultiplayer && (
          <div className={`live-leaderboard${leaderboardFlash ? ' leaderboard-flash' : ''}`}>
            <div className="leaderboard-title">🏆 LEADERBOARD 🏆</div>
            <div className="leaderboard-entries">
              {(() => {
                const rows = [];
                // Deduplicate: API returns all games per player sorted best-first,
                // so the first occurrence of each username is their best result.
                const seen = new Set();
                const bestPerPlayer = liveRankings.filter(r => {
                  if (seen.has(r.username)) return false;
                  seen.add(r.username);
                  return true;
                });
                const finishedUsernames = new Set(bestPerPlayer.map(r => r.username));

                for (const entry of bestPerPlayer) {
                  if (rows.length >= 3) break;
                  rows.push(
                    <div key={`f-${entry.username}`} className={`leaderboard-entry finished${entry.username === username ? ' self' : ''}`}>
                      <span className="entry-medal">{RANK_MEDALS[rows.length] ?? `#${rows.length + 1}`}</span>
                      <span className="entry-username">{entry.username}</span>
                      <span className="entry-score">
                        {gameMode === GAME_MODES.INFINITE
                          ? `${entry.click_count} clicks`
                          : `${entry.mean_absolute_error?.toFixed(0)} ms`}
                      </span>
                    </div>
                  );
                }
                for (const u of (usernames ?? [])) {
                  if (rows.length >= 3) break;
                  if (!finishedUsernames.has(u)) {
                    rows.push(
                      <div key={`p-${u}`} className={`leaderboard-entry playing${u === username ? ' self' : ''}`}>
                        <span className="entry-medal">⏳</span>
                        <span className="entry-username">{u}</span>
                        <span className="entry-score playing-label">clicking...</span>
                      </div>
                    );
                  }
                }
                return rows;
              })()}
            </div>
          </div>
        )}
      </div>

      <div className="game-content">
        {/* Game mode toggle — hidden in multiplayer (mode is fixed by the room) */}
        {!isMultiplayer && (
          <div className="mode-toggle">
            <button
              className={`mode-button ${gameMode === GAME_MODES.FINITE ? 'active' : ''}`}
              onClick={() => handleGameModeChange(GAME_MODES.FINITE)}
              disabled={isRunning}
            >
              {isAnyAscii ? '[Fixed clicks]' : 'Fixed clicks'}
            </button>
            <button
              className={`mode-button ${gameMode === GAME_MODES.INFINITE ? 'active' : ''}`}
              onClick={() => handleGameModeChange(GAME_MODES.INFINITE)}
              disabled={isRunning}
            >
              {isAnyAscii ? (gameMode === GAME_MODES.INFINITE ? '[■ 1s life]' : '[1s life]') : '1s life'}
            </button>
          </div>
        )}

        {/* Time horizon buttons — only shown in finite mode */}
        {gameMode === GAME_MODES.FINITE && (
          <div className="settings-buttons">
            <div className="time-horizon-buttons" style={{ display: 'flex', width: '100%', gap: '8px' }}>
              {[10, 20, 30].map(t => {
                const isSelected = selectedTimeHorizon === t;
                const isLocked = isMultiplayer && !isSelected;
                return (
                  <button
                    key={t}
                    className={`hori-button-${t}${isSelected ? ' selected' : ''}`}
                    onClick={() => !isMultiplayer && handleTimeHorizonChange(t)}
                    style={{ flex: 1, minWidth: 0, opacity: isLocked ? 0.4 : 1, cursor: isMultiplayer ? 'default' : 'pointer' }}
                    disabled={isRunning || isLocked}
                    title={isMultiplayer && isSelected ? 'Time horizon locked by room host' : undefined}
                  >
                    {isAnyAscii ? `[${t}s${isMultiplayer && isSelected ? ' 🔒' : ''}]` : `${t}s${isMultiplayer && isSelected ? ' 🔒' : ''}`}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div style={{ width: '100%' }}>
          {gameMode === GAME_MODES.FINITE ? (
            <ProgressCircles
              clickCount={clickCount}
              timeArray={timeArray}
              selectedTimeHorizon={selectedTimeHorizon}
              isSoloMode={!isMultiplayer}
            />
          ) : (
            <LifeBar
              remainingLife={modeState.remainingLife}
              clickCount={clickCount}
              timeArray={timeArray}
            />
          )}
        </div>

        <div className="controls-button">
          <div className="button-container">
            {isAnyAscii ? (
              <div style={{ fontFamily: '"Courier New", Courier, monospace', color: asciiColor, textAlign: 'center', lineHeight: 1.4, userSelect: 'none' }}>
                <div>{'┌' + '─'.repeat(22) + '┐'}</div>
                <div style={{ display: 'flex', alignItems: 'stretch' }}>
                  <span style={{ padding: '0 2px' }}>│</span>
                  <button
                    onClick={handleClick}
                    style={{ flex: 1, border: 'none', background: 'none', color: asciiColor, fontFamily: '"Courier New", Courier, monospace', cursor: 'pointer', padding: '10px 20px', fontSize: '1.1rem', minWidth: '180px', textAlign: 'center' }}
                  >
                    {textInButton}
                  </button>
                  <span style={{ padding: '0 2px' }}>│</span>
                </div>
                <div>{'└' + '─'.repeat(22) + '┘'}</div>
              </div>
            ) : (
              <button
                className={`click-me-button ${isRunning ? 'running' : ''}`}
                onClick={handleClick}
              >
                {textInButton}
              </button>
            )}
          </div>
          <button
            className="try-again-button"
            onClick={handleTryAgain}
          >
            {isAnyAscii ? '[ Try Again ]' : 'Try Again'}
          </button>
        </div>
      </div>

      {/* Visualization Modal */}
      <VisualizationModal
        isOpen={isVisualizationModalOpen}
        onClose={handleVisualizationModalClose}
        timeArray={timeArray}
        selectedTimeHorizon={modalTimeHorizon}
        fireWorksActive={
          gameMode === GAME_MODES.INFINITE
            ? (currentScore?.clickCount ?? 0) >= 20
            : (currentScore?.mae ?? Infinity) <= 25
        }
        currentScore={currentScore}
      />

      {/* Finish notification bubble */}
      {finishNotification && (
        <div className="finish-bubble">
          {finishNotification}
        </div>
      )}

      {/* Ranking Modal */}
      {isMultiplayer && (
        <RankingModal
          isOpen={isRankingModalOpen}
          onClose={() => setIsRankingModalOpen(false)}
          rankingData={rankingData}
          highlightedGameId={highlightedGameId}
          roomId={roomId}
          roomName={roomName}
          userId={userId}
          username={username}
          gameMode={gameMode}
        />
      )}

      <style jsx>{`
        .game-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2rem;
          width: 100%;
        }

        .header-section {
          margin-bottom: 10px;
          width: 100%;
        }

        .live-leaderboard {
          margin-top: 16px;
          background: #fffef7;
          border: 2px solid #fda085;
          border-radius: 14px;
          padding: 14px 18px;
          min-width: 280px;
          box-shadow: 0 4px 20px rgba(253, 160, 133, 0.3);
          transition: box-shadow 0.3s ease, border-color 0.3s ease;
        }

        @keyframes leaderboard-flash {
          0%   { box-shadow: 0 4px 20px rgba(253,160,133,0.3); border-color: #fda085; }
          30%  { box-shadow: 0 4px 40px rgba(246,211,101,0.8); border-color: #f6d365; }
          60%  { box-shadow: 0 4px 28px rgba(246,211,101,0.4); border-color: #f6a035; }
          100% { box-shadow: 0 4px 20px rgba(253,160,133,0.3); border-color: #fda085; }
        }

        .leaderboard-flash {
          animation: leaderboard-flash 1.2s ease-out forwards;
        }

        .leaderboard-title {
          font-size: 0.8rem;
          font-weight: 800;
          letter-spacing: 0.15em;
          color: #c2410c;
          text-align: center;
          margin-bottom: 10px;
        }

        .leaderboard-entries {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .leaderboard-entry {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 6px 10px;
          border-radius: 8px;
          font-size: 0.9rem;
          transition: background 0.2s;
        }

        .leaderboard-entry.finished {
          background: rgba(246, 211, 101, 0.15);
        }

        .leaderboard-entry.playing {
          background: rgba(0, 0, 0, 0.03);
          opacity: 0.8;
        }

        .leaderboard-entry.self {
          background: rgba(246, 211, 101, 0.32);
          border: 1px solid #fda085;
          opacity: 1 !important;
        }

        .entry-medal {
          font-size: 1.1rem;
          min-width: 24px;
          text-align: center;
        }

        .entry-username {
          flex: 1;
          color: #1a202c;
          font-weight: 600;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .entry-score {
          color: #276749;
          font-weight: 700;
          font-size: 0.85rem;
          white-space: nowrap;
        }

        .playing-label {
          color: #9a7c5f;
          font-weight: 400;
          font-style: italic;
          font-size: 0.78rem;
        }

        @keyframes bubble-in-out {
          0%   { opacity: 0; transform: translateX(-50%) translateY(16px) scale(0.85); }
          15%  { opacity: 1; transform: translateX(-50%) translateY(0)    scale(1.05); }
          25%  { transform: translateX(-50%) translateY(0) scale(1); }
          75%  { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
          100% { opacity: 0; transform: translateX(-50%) translateY(-12px) scale(0.9); }
        }

        .finish-bubble {
          position: fixed;
          bottom: 36px;
          left: 50%;
          transform: translateX(-50%);
          background: #fffef7;
          border: 2px solid #fda085;
          border-radius: 999px;
          padding: 12px 24px;
          font-size: 1rem;
          color: #1a202c;
          box-shadow: 0 8px 30px rgba(253, 160, 133, 0.45);
          animation: bubble-in-out 3s ease forwards;
          white-space: nowrap;
          z-index: 9999;
          pointer-events: none;
        }

        .mode-toggle {
          display: flex;
          gap: 0;
          border: 2px solid #CBD5E0;
          border-radius: 8px;
          overflow: hidden;
        }

        .mode-button {
          padding: 8px 20px;
          background: white;
          border: none;
          white-space: nowrap;
          cursor: pointer;
          font-size: 0.95rem;
          color: #4A5568;
          transition: background-color 0.15s, color 0.15s;
        }

        .mode-button:first-child {
          border-right: 1px solid #CBD5E0;
        }

        .mode-button.active {
          background-color: #2196F3;
          color: white;
          font-weight: 600;
        }

        .mode-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .click-me-button.running {
          animation: pulse 0s infinite;
        }

        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default Game;
